import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import colors from 'sass/variables/styles.module.scss'
import useComponentSize from 'hooks/useComponentSize'
import styles from './styles.module.scss'

const AngledBg = ({
  className,
  side,
  deg,
  origin,
  background,
  width,
  opacity,
  image,
  imagePosition,
  imageOpacity,
}) => {
  const angledBgStyles = classNames(className, {
    [styles.angledBg]: true,
    [styles.left]: side === 'left',
    [styles.bottom]: origin === 'bottom',
  })

  const angledBgRef = useRef()
  const imageRef = useRef()
  const size = useComponentSize(angledBgRef)
  // size == { width: 100, height: 200 }
  const { height: angledHeight } = size

  useEffect(() => {
    const angledElement = angledBgRef.current
    const imgElement = imageRef.current
    //   Convert degrees to radians and take 1/sine to cosecant
    // 90-28deg = 62
    const cosecant = 1 / Math.sin((90 - deg) * (Math.PI / 180))
    const hypotenuse = cosecant * angledHeight
    const angledWidthSqrd =
      hypotenuse * hypotenuse - angledHeight * angledHeight
    const angledWidth = Math.round(Math.sqrt(angledWidthSqrd) / 2)

    //   Inline style adjustments
    if ((side === 'right' && deg > 0) || (side === 'left' && deg < 0)) {
      angledElement.style.width = `calc(1${width}% + ${angledWidth}px)`
      if (image) imgElement.style.right = `-${angledWidth}px`
    } else if ((side === 'right' && deg < 0) || (side === 'left' && deg > 0)) {
      angledElement.style.width = `calc(1${width}% - ${angledWidth}px)`
      if (image) imgElement.style.left = `-${angledWidth}px`
    }
  }, [angledHeight, deg, image, side, width])

  return (
    <>
      <div
        ref={angledBgRef}
        className={angledBgStyles}
        style={{ transform: `skew(${deg}deg)`, background, opacity }}
      >
        {/* Skew image reverse of parent */}
        {image && (
          <div
            ref={imageRef}
            className={styles.image}
            style={{
              background: `url('${image}')`,
              backgroundPosition: imagePosition,
              transform: `skew(${deg * -1}deg)`,
              opacity: imageOpacity,
            }}
          />
        )}
      </div>
    </>
  )
}

AngledBg.defaultProps = {
  side: 'right',
  deg: -28,
  origin: 'top',
  width: '150%',
  opacity: 1,
  background: colors.headingBlue,
  image: null,
  imagePosition: null,
  imageOpacity: null,
}

AngledBg.propTypes = {
  /**
   * Which side you want the angled background on.
   */
  side: PropTypes.oneOf(['left', 'right']),
  /**
   * The angle that you want the background to be
   */
  deg: PropTypes.number,
  /**
   * The angle opacity
   */
  opacity: PropTypes.number,
  /**
   * Origin of the angle
   */
  origin: PropTypes.oneOf(['top', 'bottom']),
  /**
   * Width of the angled side
   */
  width: PropTypes.string,
  /**
   * Pass any valid CSS background property (i.e. color, image, etc)
   */
  background: PropTypes.string,
  /**
   * Pass in any Webpack loaded image
   */
  image: PropTypes.string,
  /**
   * Pass in valid CSS Background position
   */
  imagePosition: PropTypes.string,
  /**
   * Pass in valid CSS Background position
   */
  imageOpacity: PropTypes.number,
}

export default AngledBg
